import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getReservations, type ReservationListItem } from '../../../services/reservationsApi';
import { useAuth } from '../../../contexts/AuthContext';
import {
  formatReservationDate,
  formatReservationDateTime,
} from '../utils/reservationMapper';
import { filterConsumedReservations } from '../utils/reservationFilters';
import { useTranslation } from 'react-i18next';

const EntryExitLog: React.FC = () => {
  const { t } = useTranslation('other');
  const { getBranchName } = useAuth();
  const [rows, setRows] = useState<ReservationListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLogs = useCallback(async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getReservations({
          type: 'ticket',
          page: 1,
          pageSize: 300,
        });
        setRows(response.rows);
      } catch (err) {
        console.error('Failed to load entry exit log:', err);
        setError(t('reservations.entryExit.errors.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    }, [t]);
  
  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const consumedRows = useMemo(() => filterConsumedReservations(rows), [rows]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6">{t('reservations.entryExit.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        {t('reservations.entryExit.subtitle')}
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', backgroundColor: '#fff' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.entryExit.table.reservationNo')}</TableCell>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.entryExit.table.customer')}</TableCell>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.entryExit.table.branch')}</TableCell>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.entryExit.table.reservationDate')}</TableCell>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.entryExit.table.recordTime')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {consumedRows.map((row) => (
                <TableRow key={`${row.reservationType}-${row.id}`}>
                  <TableCell>{row.reservationId}</TableCell>
                  <TableCell>{row.customerName || '-'}</TableCell>
                  <TableCell>{getBranchName(row.branchId) || '-'}</TableCell>
                  <TableCell>{formatReservationDate(row.reservationDate)}</TableCell>
                  <TableCell>{formatReservationDateTime(row.updatedAt || row.createdAt)}</TableCell>
                </TableRow>
              ))}
              {consumedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {t('reservations.entryExit.empty')}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default EntryExitLog; 